import { useState, type ReactNode } from 'react';
import { CodeEditor } from './CodeEditor';
import { InlineDocEditor } from './InlineDocEditor';

/** Trecho selecionado. `from`/`to` (offsets) só existem no modo Código; nos demais
 * vem só o `text` (usado como foco da pergunta à IA). */
export interface MdSelection {
  text: string;
  from?: number;
  to?: number;
}

/** Captura o texto selecionado em QUALQUER modo: textarea/input (edição de bloco) ou
 * texto renderizado (Visualização). O CodeEditor reporta a própria seleção (com offsets). */
function captureSelectionText(): string {
  const ae = document.activeElement;
  if (ae instanceof HTMLTextAreaElement || ae instanceof HTMLInputElement) {
    const { selectionStart, selectionEnd, value } = ae;
    if (selectionStart != null && selectionEnd != null && selectionEnd > selectionStart) {
      return value.slice(selectionStart, selectionEnd);
    }
  }
  return window.getSelection()?.toString() ?? '';
}

/**
 * Editor de Markdown REUTILIZÁVEL com toggle Visualização (Obsidian) / Código.
 *
 * - **Visualização**: `InlineDocEditor` (edita o MD renderado por bloco; auto-save).
 * - **Código**: `CodeEditor` (CodeMirror, MD cru).
 * - **Seleção em qualquer modo**: no Código vem com offsets (`onSelect` com from/to →
 *   permite substituir o trecho); na Visualização/edição-de-bloco vem só o texto
 *   (foco da pergunta). Mesmo componente na base de docs e na biblioteca de skills.
 */
export function MarkdownEditor({
  value,
  onChange,
  onSaveBlock,
  onSelect,
  readOnly = false,
  height = '64vh',
  initialMode = 'preview',
  rightSlot,
}: {
  value: string;
  onChange: (v: string) => void;
  onSaveBlock: (v: string) => void;
  onSelect?: (sel: MdSelection) => void;
  readOnly?: boolean;
  height?: string;
  initialMode?: 'preview' | 'code';
  rightSlot?: ReactNode;
}) {
  const [mode, setMode] = useState<'preview' | 'code'>(initialMode);

  const handleDomSelect = () => {
    if (!onSelect) return;
    const text = captureSelectionText();
    if (text.trim()) onSelect({ text });
  };

  return (
    <div className="md-editor">
      <div className="md-editor-bar">
        <div className="seg-toggle" role="group" aria-label="Modo do editor">
          <button type="button" className={`seg-btn ${mode === 'preview' ? 'is-active' : ''}`} onClick={() => setMode('preview')}>
            Visualização
          </button>
          <button type="button" className={`seg-btn ${mode === 'code' ? 'is-active' : ''}`} onClick={() => setMode('code')}>
            Código
          </button>
        </div>
        {rightSlot && <div className="md-editor-actions">{rightSlot}</div>}
      </div>

      {mode === 'preview' ? (
        <div className="md-editor-preview" onMouseUp={handleDomSelect} onKeyUp={handleDomSelect}>
          <InlineDocEditor value={value} onSave={onSaveBlock} readOnly={readOnly} />
        </div>
      ) : (
        <CodeEditor
          value={value}
          onChange={onChange}
          readOnly={readOnly}
          height={height}
          {...(onSelect ? { onSelection: (s) => onSelect({ text: s.text, from: s.from, to: s.to }) } : {})}
        />
      )}
    </div>
  );
}
